// HTTP client for the three endpoints the SPA hits during a scan upload.
//
// Wire contract (2026-05-22 footscan-server split — see top-level README +
// CLAUDE.md). Two services, one customer-facing domain:
//
//   1. GET    https://ops.hezo-cycling.com/api/scan-sessions/<token>
//      (hezo-ops; JWT-in-URL; returns slim { customerId, scanID })
//
//   2. POST   https://ops.hezo-cycling.com/api/scan-sessions/<token>/presigned-uploads
//      (hezo-ops; JWT-in-URL; body { filenames: [...] };
//       returns { presignedPuts: { "<filename>": "<url>", ... } })
//
//   3. POST   https://ops.hezo-cycling.com/scan-process/scan-sessions/<scanID>/process
//      (footscan-server, path-routed by the hezo-ops ALB to a Lambda target
//       group; Authorization: Bearer <same JWT>; body { customerId })
//
//   + PUT    <presigned S3 URL>
//      (bare PUT, URL is the auth)
//
// The two hezo-ops endpoints and the footscan-server trigger don't exist
// yet — they land in parallel sessions (hezo-ops-scan-session-slim/ +
// infrastructure-aws-footscan-server/ + footscan-server-bootstrap/). Until
// they do, this client will 404 / 401 against staging — that's expected
// and the Scan-App UI surfaces the failures so the operator sees the gap.

/**
 * Discriminator emitted by hezo-ops's slim session endpoint. Tells the
 * SPA which capture-flow to render and which inputs to expect:
 *   - "self-capture" (default) — end-customer with smartphone runs the
 *     camera-capture flow (the DEBUG-only simulate path fakes the bytes
 *     for device-free staging runs)
 *   - "volumental" — partner studio scans the customer with Volumental
 *     hardware and uploads the resulting OBJ file(s) via a file-picker
 *
 * The value originates on Pair.scanFlow inside hezo-ops, travels into
 * the JWT as a `flow` claim, and surfaces here in the session payload.
 * footscan-server reads the same claim to pick its processing branch.
 *
 * Older deploys without the claim default to "self-capture" — the SPA
 * mirrors that default if the field is absent.
 */
export type ScanFlow = "self-capture" | "volumental";

export interface ScanSession {
  customerId: string;
  scanID: string;
  flow: ScanFlow;
}

/**
 * A HEZO shoe has no single colour — it's manufactured as separate printed
 * parts (OUTER/INNER/PLUGS) plus a non-printed BAND strap, each with its own
 * colour. hezo-ops stores the printed-part colours on `Part.color` and the
 * strap on the denormalised `Pair.bandColor`. These are the four colour
 * "slots" the operator UI shows as O·I·P·B swatches.
 */
export type ColourSlot = "OUTER" | "INNER" | "PLUGS" | "BAND";
export const COLOUR_SLOTS: readonly ColourSlot[] = ["OUTER", "INNER", "PLUGS", "BAND"];

/**
 * A resolved slot colour. `key` is hezo-ops's canonical colour key (#715) —
 * the SPA localises the display name off it; `name` is the raw catalogue name
 * (fallback when a key isn't translated); `hex` is the CSS colour.
 */
export interface PairColour {
  key: string;
  name: string;
  hex: string;
}

/** Per-slot colours; a slot is absent/null when unset for this pair. */
export type PairColours = Partial<Record<ColourSlot, PairColour>>;

/**
 * One selectable pair inside a reusable multi-pair invite. Fields originate
 * on hezo-ops's `Pair`. Custom shoes carry no size, so the customer
 * disambiguates by model (`shoeType`) + the full colour breakdown
 * (`colours`) + `recipientName` + `position`; the Shopify order fields +
 * `state` are operator/debug context only.
 *
 * `colours` is the enriched per-slot breakdown (added to the payload once
 * hezo-ops resolves the pair's Parts). `colour` is the legacy single BAND
 * colour (`Pair.bandColor`); still parsed for compatibility but no longer
 * rendered — it carries no hex and showing only the strap misreads as "the
 * shoe colour" (see PairPicker). The picker shows colours only when the
 * enriched `colours` field is present.
 */
export interface ScanPair {
  id: string;
  recipientName: string;
  position: string;
  shoeType: string;
  colour: string;
  colours?: PairColours;
  state: string;
  shopifyOrderNumber: string;
  shopifyOrderName: string;
  /**
   * Scan runs this pair already has and needs (hezo-ops SCAN_MODEL_FLOW
   * § Phase 3). Each run is its own /start with a fresh scanID; the pair stays
   * in `pairs` until `runsDone >= runsRequired`. Both absent on an invite
   * minted before the feature: the SPA then does one run, as before.
   */
  runsDone?: number;
  runsRequired?: number;
}

/**
 * The two shapes `GET /api/scan-sessions/<token>` can return, discriminated
 * by `mode`:
 *
 *   - "multi-pair" (reusable invite) — the link covers N pairs on an order;
 *     the customer picks which pair(s) this scan is for, then POSTs /start
 *     to open a ScanSession. `done: true` means every pair has already been
 *     scanned → the SPA shows a "thank you, nothing left" screen instead of
 *     the picker. A pairless invite (walk-in, campaign, complaint) answers
 *     `pairs: []` with `done: false` and counts its runs on the invite.
 *   - "legacy" — the classic single-scan invite; the scanID is baked into
 *     the GET response and the SPA jumps straight into the capture flow. The
 *     server omits `mode` here; we synthesise `mode: "legacy"`.
 */
export type SessionResponse =
  | { mode: "legacy"; customerId: string; scanID: string; flow: ScanFlow }
  | {
      mode: "multi-pair";
      customerId: string;
      flow: ScanFlow;
      pairs: ScanPair[];
      done: boolean;
      /** Invite-level run count (lowest pair count on a multi-pair invite).
       *  Absent on invites minted before scan runs existed. */
      runsDone?: number;
      runsRequired?: number;
    };

export interface StartSessionResponse {
  scanID: string;
  /**
   * Scan JWT for exactly this session (claims `customerId`, `scanId`,
   * `purpose`, `flow`), which footscan-server's /process and /status accept.
   * The invite token cannot be used there: it carries no `scanId`. Absent
   * from hezo-ops builds older than hezo-ops#1684.
   */
  scanToken?: string;
}

export interface PresignedUploadsResponse {
  presignedPuts: Record<string, string>;
}

export interface ProcessTriggerSyncOk {
  ok: true;
  scanID: string;
  outputCount?: number;
}

export interface ProcessTriggerAsyncOk {
  ok: true;
  scanID: string;
  expectedSeconds: number;
}

/** One customer-facing problem from footscan-server's structured error
 *  contract (`src/pipeline/errors.py`). `error` is a stable code
 *  (`foot_not_detected`, `sock_or_shoe_suspected`, ...), `message` is
 *  finished German customer copy — the SPA never translates codes itself. */
export interface ScanIssue {
  error: string;
  photo: number;
  foot: string;
  message: string;
}

export type ProcessTriggerResult =
  | {
      kind: "ok";
      status: 200;
      body: ProcessTriggerSyncOk;
      /**
       * True only when the server confirmed the reconstruction itself
       * finished: a sync 200 from `/process`, or a poll that settled `ok`.
       * The notify-only fallback (a reusable session without a scanToken)
       * answers 200 without any reconstruction, so it leaves this unset and
       * DoneScreen keeps the "we're still building it" copy.
       */
      processed?: boolean;
    }
  | { kind: "accepted"; status: 202; body: ProcessTriggerAsyncOk }
  | {
      kind: "error";
      status: number;
      body: string;
      customerIssues?: ScanIssue[];
      /**
       * Where the failure happened, because the two need different customer
       * copy. `trigger` (the default when absent) means the POST itself never
       * got through, so processing never started. `processing` means the POST
       * was accepted with a 202 and the reconstruction then failed, which the
       * SPA learns from polling `GET .../status`. Telling that customer the
       * scan was "not fully submitted" is simply wrong.
       */
      stage?: "trigger" | "processing";
    };

/**
 * footscan-server's structured error contract puts every problem found on a
 * validation-failure body's `errors` array (in capture order — left before
 * right, photo 1 before 3), never empty when present. The four top-level
 * fields (`error`, `photo`, `foot`, `message`) repeat the first entry for an
 * older server that doesn't send `errors` yet — that's the fallback below.
 */
function parseCustomerIssues(body: string): ScanIssue[] | undefined {
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return undefined;
  }
  if (!parsed || typeof parsed !== "object") return undefined;
  const p = parsed as Record<string, unknown>;

  const toIssue = (raw: unknown): ScanIssue | undefined => {
    if (!raw || typeof raw !== "object") return undefined;
    const r = raw as Record<string, unknown>;
    if (typeof r.message !== "string" || r.message.trim() === "") return undefined;
    return {
      error: typeof r.error === "string" ? r.error : "",
      photo: typeof r.photo === "number" ? r.photo : 0,
      foot: typeof r.foot === "string" ? r.foot : "",
      message: r.message,
    };
  };

  if (Array.isArray(p.errors) && p.errors.length > 0) {
    const issues = p.errors.map(toIssue).filter((i): i is ScanIssue => i !== undefined);
    if (issues.length > 0) return issues;
  }
  const fallback = toIssue(p);
  return fallback ? [fallback] : undefined;
}

/**
 * Outcome of a `GET .../status` poll (see `fetchScanStatus`). `pending` means
 * keep polling; `unknown` covers a transient read failure (network blip, a
 * race against the status write) that should also just keep polling rather
 * than surface as a hard error: the accept request already returned 202, so
 * a momentarily-missing status read is not itself a failure.
 */
export type ScanStatusResult =
  | { kind: "pending" }
  | { kind: "ok"; outputCount?: number; flow?: ScanFlow }
  | { kind: "failed"; issues: ScanIssue[]; reason?: string }
  | { kind: "unknown" };

/**
 * GET <base>/scan-process/scan-sessions/<scanID>/status?customerId=<uuid>.
 * Polled after a 202-accepted trigger so the SPA learns the real outcome
 * without holding the original request open for the ~150s reconstruction
 * (footscan-server docs/architecture.md § Async dispatch). Auth mirrors
 * `triggerScanProcess`: same Bearer JWT, `customerId` travels as a query
 * param since GET has no body.
 *
 * The response body is `processed/status.json` verbatim: `{state:"pending"}`,
 * `{state:"ok", outputCount, flow}`, or the same shape a 422 body carries
 * (`{state:"failed", error, photo, foot, message, errors}`); `errors` reuses
 * `parseCustomerIssues` so a failed reconstruction lists every problem the
 * same way a synchronous validation failure already does.
 */
/** Deadline for one status poll. Polls run every 3s, so a slow one is cheaper
 *  to drop and re-ask than to wait out. */
export const STATUS_TIMEOUT_MS = 15_000;

export async function fetchScanStatus(
  baseUrl: string,
  token: string,
  scanID: string,
  customerId: string,
): Promise<ScanStatusResult> {
  const url = `${baseUrl}/scan-process/scan-sessions/${encodeURIComponent(scanID)}/status?customerId=${encodeURIComponent(customerId)}`;
  let resp: Response;
  let body: string;
  try {
    // A poll that never answers would hold the poller's in-flight flag
    // forever, and the 4-minute cap is only checked between ticks, so the
    // customer would sit on the "accepted" copy for good. A timeout folds into
    // "unknown", which just schedules the next tick.
    [resp, body] = await withTimeout(STATUS_TIMEOUT_MS, undefined, async (signal) => {
      const r = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });
      return [r, r.ok ? await r.text().catch(() => "") : ""] as const;
    });
  } catch {
    return { kind: "unknown" };
  }
  if (!resp.ok) return { kind: "unknown" };
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return { kind: "unknown" };
  }
  if (!parsed || typeof parsed !== "object") return { kind: "unknown" };
  const p = parsed as Record<string, unknown>;
  if (p.state === "ok") {
    return {
      kind: "ok",
      outputCount: typeof p.outputCount === "number" ? p.outputCount : undefined,
      flow: p.flow === undefined ? undefined : normaliseFlow(p.flow),
    };
  }
  if (p.state === "failed") {
    const reason = typeof p.error === "string" ? p.error : undefined;
    // `internal_error` is the unexpected-fault class: whatever `message` it
    // carries is a bare exception type name meant for operator triage, never
    // customer copy (footscan-server `docs/hezo-ops-notify-contract.md`).
    // Dropping it here is what makes the DoneScreen fall back to its generic
    // "processing failed" wording instead of showing the customer "ValueError".
    const issues = reason === "internal_error" ? [] : (parseCustomerIssues(body) ?? []);
    return { kind: "failed", issues, reason };
  }
  return { kind: "pending" };
}

/**
 * Whether a failed process trigger is worth retrying as-is. A 4xx (and the
 * synthetic redirect/auth-gate status) is a validation/client error — retried
 * with the same already-uploaded photos it fails identically every time, so
 * the customer needs to redo the capture instead. A 5xx or network error
 * (`status: 0`) is potentially transient and worth a plain retry.
 */
export function isRecoverableTriggerError(status: number): boolean {
  return status === 0 || status >= 500;
}

/**
 * Map a `processed/status.json` failure reason onto the HTTP status
 * footscan-server answers with for the same failure class on its synchronous
 * path (`src/app.py`): a bad-input rejection is a 422, an unexpected fault
 * (`internal_error`: bug, OOM, model-load or S3 blip) a 500. A polled
 * failure arrives over a 200 GET and so carries no status of its own; this
 * keeps the one the trigger result reports honest instead of hardcoding 422
 * and classifying every crash as unretryable. footscan-server re-runs a scan
 * whose recorded state is `failed`, so a retry is a real recovery there.
 */
export function statusForFailureReason(reason: string | undefined): number {
  return reason === "internal_error" ? 500 : 422;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** A request got no answer within its deadline and was aborted. */
export class RequestTimeoutError extends Error {
  constructor(public timeoutMs: number) {
    super(`no response within ${timeoutMs}ms`);
    this.name = "RequestTimeoutError";
  }
}

/**
 * Run `request` against an abort signal that fires after `timeoutMs`, or when
 * `outer` aborts, whichever comes first. The whole callback is covered, body
 * read included, so a response that stalls mid-stream still times out. A
 * timeout surfaces as `RequestTimeoutError`; a caller abort keeps the
 * browser's own `AbortError`, so the two stay distinguishable for retries.
 *
 * `AbortSignal.any` / `AbortSignal.timeout` would say this in one line, but
 * `any` needs iOS Safari 17.4 and the scan runs on whatever phone the
 * customer has.
 */
async function withTimeout<T>(
  timeoutMs: number,
  outer: AbortSignal | undefined,
  request: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  const forward = (): void => controller.abort();
  if (outer?.aborted) controller.abort();
  else outer?.addEventListener("abort", forward, { once: true });
  try {
    return await request(controller.signal);
  } catch (err) {
    if (timedOut) throw new RequestTimeoutError(timeoutMs);
    throw err;
  } finally {
    clearTimeout(timer);
    outer?.removeEventListener("abort", forward);
  }
}

/** Fold an unknown/absent `flow` field to the safe self-capture default. */
function normaliseFlow(flow: unknown): ScanFlow {
  return flow === "volumental" ? "volumental" : "self-capture";
}

/** Only accept a clean `#rgb`/`#rrggbb` hex so it's safe in an inline style. */
function sanitiseHex(raw: unknown): string | null {
  const s = String(raw ?? "").trim();
  return /^#?[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(s)
    ? s.startsWith("#")
      ? s
      : `#${s}`
    : null;
}

/** Parse the enriched per-slot colours, if the payload carries them. */
function normalisePairColours(raw: unknown): PairColours | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const src = raw as Record<string, unknown>;
  const out: PairColours = {};
  for (const slot of COLOUR_SLOTS) {
    const entry = src[slot];
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Record<string, unknown>;
    const name = String(e.name ?? "").trim();
    const key = String(e.key ?? "").trim();
    const hex = sanitiseHex(e.hex);
    // A slot needs at least a name to be worth showing; hex may be absent
    // (unknown colour) — the UI then renders a neutral swatch.
    if (name) out[slot] = { key, name, hex: hex ?? "" };
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Coerce one raw pair object from the GET payload into a `ScanPair`. */
function normalisePair(raw: unknown): ScanPair {
  const p = (raw ?? {}) as Record<string, unknown>;
  return {
    id: String(p.id ?? ""),
    recipientName: String(p.recipientName ?? ""),
    position: String(p.position ?? ""),
    shoeType: String(p.shoeType ?? ""),
    colour: String(p.colour ?? ""),
    colours: normalisePairColours(p.colours),
    state: String(p.state ?? ""),
    shopifyOrderNumber: String(p.shopifyOrderNumber ?? ""),
    shopifyOrderName: String(p.shopifyOrderName ?? ""),
    ...runCounts(p),
  };
}

/** `runsDone` / `runsRequired` when the payload carries both as sane numbers.
 *  One without the other is treated as absent, i.e. a single run. */
function runCounts(raw: Record<string, unknown>): { runsDone?: number; runsRequired?: number } {
  const done = raw.runsDone;
  const required = raw.runsRequired;
  if (
    typeof done !== "number" ||
    typeof required !== "number" ||
    !Number.isInteger(done) ||
    !Number.isInteger(required) ||
    done < 0 ||
    required < 1
  ) {
    return {};
  }
  return { runsDone: done, runsRequired: required };
}

/**
 * GET /api/scan-sessions/<token>. Resolves the JWT into either a reusable
 * multi-pair payload (`mode: "multi-pair"` + `pairs` + `done`) or the legacy
 * single-scan payload (scanID baked in). Discriminated on the presence of
 * `mode === "multi-pair"`.
 *
 * Error mapping is the caller's job; notable statuses: 401 invalid/revoked,
 * 410 already-processed/orphaned invite, 404 not found.
 */
export async function fetchScanSession(
  baseUrl: string,
  token: string,
): Promise<SessionResponse> {
  const url = `${baseUrl}/api/scan-sessions/${encodeURIComponent(token)}`;
  const resp = await fetch(url, { method: "GET" });
  const body = await resp.text();
  if (!resp.ok) {
    throw new ApiError(
      `fetchScanSession: ${resp.status} ${resp.statusText}`,
      resp.status,
      body.slice(0, 400),
    );
  }
  let parsed: {
    customerId?: string;
    scanID?: string;
    flow?: unknown;
    mode?: unknown;
    pairs?: unknown;
    done?: unknown;
    runsDone?: unknown;
    runsRequired?: unknown;
  };
  try {
    parsed = JSON.parse(body);
  } catch (err) {
    throw new ApiError(
      `fetchScanSession: invalid JSON — ${(err as Error).message}`,
      resp.status,
      body.slice(0, 400),
    );
  }
  const flow = normaliseFlow(parsed.flow);
  const customerId = String(parsed.customerId ?? "");
  if (parsed.mode === "multi-pair") {
    const pairs = Array.isArray(parsed.pairs) ? parsed.pairs.map(normalisePair) : [];
    return {
      mode: "multi-pair",
      customerId,
      flow,
      pairs,
      done: parsed.done === true,
      ...runCounts(parsed),
    };
  }
  // Legacy single-scan invite (no `mode` field). Defensive default on flow
  // mirrors the pre-multi-pair behaviour.
  return {
    mode: "legacy",
    customerId,
    scanID: String(parsed.scanID ?? ""),
    flow,
  };
}

/**
 * POST /api/scan-sessions/<token>/start. Reusable multi-pair only: the
 * customer commits to scanning 1..50 pairs (none on a pairless invite,
 * hezo-ops PR C); hezo-ops opens a fresh
 * ScanSession under the invite and returns its `scanID` plus a `scanToken`
 * scoped to that session. The scanID drives presigned-uploads (server infers
 * it from the active session) and the attribution notify; the scanToken
 * authorises the footscan-server /process trigger and /status polling.
 *
 * Error statuses surfaced to the caller via ApiError:
 *   400 not_reusable_invite · 409 invalid_pairs · 409 runs_complete (pairless,
 *   all runs in) · 401 invalid · 410 already_processed
 */
export async function startScanSession(
  baseUrl: string,
  token: string,
  pairIds: string[],
): Promise<StartSessionResponse> {
  const url = `${baseUrl}/api/scan-sessions/${encodeURIComponent(token)}/start`;
  const resp = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pairIds }),
  });
  const body = await resp.text();
  if (!resp.ok) {
    throw new ApiError(
      `startScanSession: ${resp.status} ${resp.statusText}`,
      resp.status,
      body.slice(0, 400),
    );
  }
  try {
    const parsed = JSON.parse(body) as { scanID?: string; scanToken?: unknown };
    return {
      scanID: String(parsed.scanID ?? ""),
      ...(typeof parsed.scanToken === "string" && parsed.scanToken
        ? { scanToken: parsed.scanToken }
        : {}),
    };
  } catch (err) {
    throw new ApiError(
      `startScanSession: invalid JSON — ${(err as Error).message}`,
      resp.status,
      body.slice(0, 400),
    );
  }
}

/**
 * POST /api/scan-sessions/<token>/presigned-uploads. Caller declares which
 * filenames it intends to upload; server validates each against
 * `^[A-Za-z0-9._\-/]+$` with no `..` (max 50 filenames per request) and
 * returns one presigned PUT URL per filename. Slashes are allowed: the
 * Volumental flow uploads to `left/model_l.<ext>` and `right/model_r.<ext>`.
 * The SPA may call this endpoint multiple times if it produces artifacts in
 * stages.
 */
export async function requestPresignedUploads(
  baseUrl: string,
  token: string,
  filenames: string[],
): Promise<PresignedUploadsResponse> {
  const url = `${baseUrl}/api/scan-sessions/${encodeURIComponent(token)}/presigned-uploads`;
  const resp = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filenames }),
  });
  const body = await resp.text();
  if (!resp.ok) {
    throw new ApiError(
      `requestPresignedUploads: ${resp.status} ${resp.statusText}`,
      resp.status,
      body.slice(0, 400),
    );
  }
  try {
    return JSON.parse(body) as PresignedUploadsResponse;
  } catch (err) {
    throw new ApiError(
      `requestPresignedUploads: invalid JSON — ${(err as Error).message}`,
      resp.status,
      body.slice(0, 400),
    );
  }
}

/**
 * Deadline for a single presigned PUT, upload body included. A 3 MB capture
 * JPEG takes around 30s on a poor 4G link, so anything much lower would kill
 * healthy uploads; anything much higher leaves a dead connection (a
 * WiFi-to-LTE handover mid-upload) freezing the progress bar for minutes.
 */
export const UPLOAD_TIMEOUT_MS = 60_000;

/**
 * PUT a single file to its presigned URL. The URL contains its own
 * signature, so no Authorization header is needed.
 *
 * One attempt only; retrying is the pipeline's call (see
 * `isRetryableUploadError`). Throws `ApiError` on a non-2xx,
 * `RequestTimeoutError` past `timeoutMs`, and the browser's `AbortError` when
 * `signal` aborts.
 */
export async function uploadFile(
  presignedUrl: string,
  blob: Blob,
  contentType: string,
  opts: { signal?: AbortSignal; timeoutMs?: number } = {},
): Promise<void> {
  const { signal, timeoutMs = UPLOAD_TIMEOUT_MS } = opts;
  await withTimeout(timeoutMs, signal, async (timeoutSignal) => {
    const resp = await fetch(presignedUrl, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body: blob,
      signal: timeoutSignal,
    });
    if (!resp.ok) {
      const body = await resp.text().catch(() => "");
      throw new ApiError(
        `uploadFile PUT: ${resp.status} ${resp.statusText}`,
        resp.status,
        body.slice(0, 400),
      );
    }
  });
}

/**
 * Whether a failed `uploadFile` is worth repeating against the same URL.
 *
 * Yes for a network drop, a timeout, and the S3 answers that mean "try again"
 * (5xx incl. 503 SlowDown, 408 RequestTimeout, 429). No for every other 4xx:
 * a 403 SignatureDoesNotMatch / expired URL is deterministic, the same URL
 * cannot start working. No for a caller abort, which is a decision, not a
 * fault.
 */
export function isRetryableUploadError(err: unknown): boolean {
  if (err instanceof ApiError) {
    return err.status >= 500 || err.status === 408 || err.status === 429;
  }
  if (err instanceof RequestTimeoutError) return true;
  if ((err as { name?: string } | null)?.name === "AbortError") return false;
  // fetch rejects a dropped connection with a bare TypeError ("Load failed" on
  // Safari, "Failed to fetch" on Chrome).
  return true;
}

/**
 * POST <base>/scan-process/scan-sessions/<scanID>/process. Authed via
 * `Authorization: Bearer <same JWT>` — footscan-server validates the JWT
 * locally against the SSM-stored signing secret (shared with hezo-ops).
 *
 * Two success shapes:
 *   200 OK   — sync, processing finished before the response returned
 *   202 Accepted — async, processing kicked off in the background
 *
 * 404 is treated as "endpoint not yet deployed" so the Scan-App UI degrades
 * gracefully while the parallel infra + footscan-server bootstrap PRs are
 * landing. Any other non-2xx is a real error.
 */
export async function triggerScanProcess(
  baseUrl: string,
  token: string,
  scanID: string,
  customerId: string,
): Promise<ProcessTriggerResult> {
  const url = `${baseUrl}/scan-process/scan-sessions/${encodeURIComponent(scanID)}/process`;
  let resp: Response;
  try {
    resp = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ customerId }),
      // Never follow redirects. If the request lands on a proxy/auth layer that
      // doesn't recognise the scan JWT (e.g. a mirror where /scan-process isn't
      // routed to footscan-server), it answers 307 → /login; a *followed*
      // redirect would return the login page as a 200 and we'd misread it as
      // success. With "manual" a 3xx surfaces as an opaqueredirect we fail on.
      redirect: "manual",
    });
  } catch (err) {
    return {
      kind: "error",
      status: 0,
      body: `network error: ${(err as Error).message}`,
    };
  }
  if (resp.type === "opaqueredirect" || (resp.status >= 300 && resp.status < 400)) {
    return {
      kind: "error",
      status: resp.status || 0,
      body: "process trigger was redirected (likely an auth/login gate) — the endpoint did not accept the request",
    };
  }
  const body = await resp.text().catch(() => "");
  if (resp.status === 202) {
    try {
      const parsed = JSON.parse(body) as ProcessTriggerAsyncOk;
      return { kind: "accepted", status: 202, body: parsed };
    } catch {
      return {
        kind: "accepted",
        status: 202,
        body: { ok: true, scanID, expectedSeconds: 0 },
      };
    }
  }
  if (resp.status === 200) {
    // A real footscan-server ack is JSON `{ ok: true, ... }`. Anything else at
    // 200 (an HTML login page, an empty body, an unrelated proxy response) is
    // NOT a successful trigger — treat it as an error rather than green-lighting.
    try {
      const parsed = JSON.parse(body) as ProcessTriggerSyncOk;
      if (parsed && parsed.ok === true) {
        return { kind: "ok", status: 200, body: parsed, processed: true };
      }
    } catch {
      // fall through to the error return below
    }
    return {
      kind: "error",
      status: 200,
      body: `unexpected 200 body (not a process-trigger ack): ${body.slice(0, 200)}`,
    };
  }
  return {
    kind: "error",
    status: resp.status,
    body: body.slice(0, 400),
    customerIssues: parseCustomerIssues(body),
  };
}

/**
 * POST /api/scan-sessions/<token>/notify. Attribution step of the reusable
 * multi-pair flow: once every PUT lands, tell hezo-ops that this invite's
 * ScanSession is complete so it binds the picked pairs. It does NOT start
 * the reconstruction; the SPA fires footscan-server's /process afterwards
 * with the session's scanToken. Body `{ scanID }`; the server guards that
 * the scanID belongs to a ScanSession under this invite.
 *
 * Result is mapped onto `ProcessTriggerResult` so the shared DoneScreen can
 * render it unchanged:
 *   200 { ok: true }        → kind "ok"
 *   200 { ok: false, reason } → kind "error", status 200 (hezo-ops's
 *                             permanent notify failure; not retryable)
 *   200 anything else       → kind "error", status 200 (not a notify ack)
 *   404 session_not_found   → kind "error" (real error, not "not deployed")
 *   410 already_processed   → kind "error"
 *   network / other non-2xx → kind "error"
 */
export async function notifyScanSession(
  baseUrl: string,
  token: string,
  scanID: string,
): Promise<ProcessTriggerResult> {
  const url = `${baseUrl}/api/scan-sessions/${encodeURIComponent(token)}/notify`;
  let resp: Response;
  try {
    resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ scanID }),
      // See triggerScanProcess: don't follow an auth redirect into a login page
      // that would masquerade as a 200 success.
      redirect: "manual",
    });
  } catch (err) {
    return {
      kind: "error",
      status: 0,
      body: `network error: ${(err as Error).message}`,
    };
  }
  if (resp.type === "opaqueredirect" || (resp.status >= 300 && resp.status < 400)) {
    return {
      kind: "error",
      status: resp.status || 0,
      body: "notify was redirected (likely an auth/login gate) — the endpoint did not accept the request",
    };
  }
  const body = await resp.text().catch(() => "");
  if (resp.status === 200) {
    // Same rule as triggerScanProcess: only a JSON `{ ok: true }` is an ack.
    // An HTML page or an empty body from a misrouted proxy/ingress is a 200
    // too, and green-lighting it would tell the customer their scan is in
    // while nothing was queued. hezo-ops also answers a permanent failure
    // with 200 `{ ok: false, reason }`; status 200 keeps that non-retryable
    // in `isRecoverableTriggerError`, matching the server's "don't retry".
    let parsed: unknown;
    try {
      parsed = JSON.parse(body);
    } catch {
      parsed = undefined;
    }
    if ((parsed as { ok?: unknown } | null)?.ok === true) {
      return { kind: "ok", status: 200, body: { ok: true, scanID } };
    }
    const reason = (parsed as { reason?: unknown } | null)?.reason;
    return {
      kind: "error",
      status: 200,
      body:
        typeof reason === "string"
          ? `notify refused: ${reason}`
          : `unexpected 200 body (not a notify ack): ${body.slice(0, 200)}`,
    };
  }
  return {
    kind: "error",
    status: resp.status,
    body: body.slice(0, 400),
    customerIssues: parseCustomerIssues(body),
  };
}
